'server-only'



////////////////////////////////////////////////////////////
//
//
//
//
//
//
//
//
//
//
//
//
//
//
// THIS WILL BYPASS RLS POLICIES AND CAN ACCESS ANY DATA IN THE DB
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
////////////////////////////////////////////////////////////





import { createAdminClient } from "@/lib/supabase/admin"
import { LogErrorParams } from "@/lib/supabase/queries/error-logs-supabase-query"
import { backupFailedErrorLog } from "@/lib/redis/queries/failed-operations-redis-query"
import { v4 as uuidv4 } from 'uuid'
import { snakeObjectToCamel } from "@/lib/utils/snake-to-camel"
import { User, Issue } from "@/lib/types"


////////////////////////////////////////////////////////////
// Users
////////////////////////////////////////////////////////////
export async function getUserFromDbAsAdmin({ username, userId, fingerprint }: { username?: string, userId?: string, fingerprint?: string }): Promise<{ data: (User & { isDangerous?: boolean }) | null, error: any }> {
    // Create admin client that bypasses RLS
    const supabaseAdmin = await createAdminClient()
    
    // Fetch system user by username or userId
    let query = supabaseAdmin.from("users").select("*")
    
    if (userId) {
        query = query.eq("id", userId)
    } else if (username) {
        query = query.eq("username", username)
    } else {
        console.error("Either username or userId must be provided")
        return { data: null, error: new Error("Either username or userId must be provided") }
    }
    
    const { data, error } = await query.single()
    
    if (error) {
        console.error("Error fetching system user:", error)
        return { data: null, error }
    }
    
    return { data: snakeObjectToCamel(data) as (User & { isDangerous?: boolean }), error: null }
}


////////////////////////////////////////////////////////////
// Errors
////////////////////////////////////////////////////////////
export async function logErrorAsAdmin({
    userId,
    message,
    type,
    status,
    stackTrace,
    additionalInfo
}: LogErrorParams): Promise<boolean> {
    try {
        const supabase = await createAdminClient()
        
        const { error } = await supabase
            .from("errors")
            .insert({
                user_id: userId,
                message,
                type: type.toLowerCase(),
                status,
                stack_trace: stackTrace,
                additional_info: JSON.stringify(additionalInfo, null, 0),
                environment: process.env.NEXT_PUBLIC_VERCEL_ENV,
            })
        
        if (error) {
            throw new Error("Failed to log error to database")
        }
        
        return true
    } catch (err) {
        // Backup the failed error log to Redis in case of any exception
        const errorId = uuidv4()
        await backupFailedErrorLog(errorId, {
            userId,
            message,
            type,
            stackTrace,
            additionalInfo, 
            logErrorError: err
        })
        return false
    }
}




////////////////////////////////////////////////////////////
// Conversations
////////////////////////////////////////////////////////////
export async function upsertChatTitleAsAdmin(conversationId: string, title: string) {
	const supabase = await createAdminClient()
	
	try {
		const { data, error } = await supabase
			.from('conversations')
			.update({
				title: title,
				updated_at: new Date().toISOString()
			})
			.eq('id', conversationId)
		
		if (error) throw error
		
		return data
	} catch (error) {
		console.error("Failed to update conversation title:", error, {conversationId, title})
		return null
	}
}

////////////////////////////////////////////////////////////
// Shortcut
////////////////////////////////////////////////////////////
export async function shortcutJobAsAdmin({
    jobId,
    status,
    data,
    finishedAt
}: {
    jobId: string,
    status: string,
    data?: any,
    finishedAt?: Date
}) {
    try {
        const supabase = await createAdminClient()
        
        if (data) {
            // Insert with provided job_id and data
            const { data: result, error } = await supabase
                .from("shortcut")
                .insert({
                    job_id: jobId,
                    status,
                    data,
                    finished_at: finishedAt || new Date()
                })
                .select()
            
            if (error) {
                console.error("Failed to insert shortcut job:", error, { jobId, status })
                throw error
            }
            
            return { data: result[0], error: null }
        } else {
            // Only update status and finished_at
            const { data: result, error } = await supabase
                .from("shortcut")
                .update({
                    status,
                    finished_at: finishedAt || new Date()
                })
                .eq('job_id', jobId)
                .select()
            
            if (error) {
                console.error("Failed to update shortcut job status:", error, { jobId, status })
                throw error
            }
            
            return { data: result[0], error: null }
        }
    } catch (err) {
        console.error("Exception in shortcutJobAsAdmin:", err)
        return { data: null, error: err }
    }
}


/**
 * Creates a new issue record in the database
 */
export async function createIssueAsAdmin(issueData: Issue) {
	try {
		const supabase = await createAdminClient()
		
		const { data, error } = await supabase
			.from('issues')
			.insert(issueData)
			.select()
			.single()
		
		if (error) throw error
		
		return { data, error: null }
	} catch (error) {
		console.error("Error creating issue in db as admin:", error)
		return { data: null, error }
	}
}

export async function getIssuesAsAdmin(searchTerms?: string[]) {
    try {
        const supabase = await createAdminClient()
        let query = supabase.from('issues').select('*')
        
        // Add search filters if terms are provided
        if (searchTerms && searchTerms.length > 0) {
            const orConditions = searchTerms
                .map(term => `contents.ilike.%${term}%`)
                .join(',')
            query = query.or(orConditions)
        }
        
        const { data, error } = await query
        
        if (error) {
            console.error("Error fetching issues as admin:", error)
            return []
        }
        
        return data || []
    } catch (err) {
        console.error("Exception in getIssuesAsAdmin:", err)
        return []
    }
}

////////////////////////////////////////////////////////////
// Logs
////////////////////////////////////////////////////////////
export interface LogEntryParams {
    type: string
    data: any
    status?: string
}

/**
 * Creates a log entry in the logs table
 * @param params Log entry parameters (type, data, status)
 * @returns The ID of the created log entry or null if creation failed
 */
export async function createLogEntryAsAdmin(params: LogEntryParams): Promise<{ id: string | null, error: any }> {
    try {
        const supabase = await createAdminClient()
        
        const { data, error } = await supabase
            .from("logs")
            .insert({
                type: params.type,
                data: params.data,
                status: params.status || null
            })
            .select("id")
            .single()
        
        if (error) {
            console.error("Failed to create log entry:", error, params)
            return { id: null, error }
        }
        
        return { id: data.id, error: null }
    } catch (err) {
        console.error("Exception in createLogEntryAsAdmin:", err)
        return { id: null, error: err }
    }
}

/**
 * Updates a log entry in the logs table by appending to its data field
 * @param logId The ID of the log entry to update
 * @param dataToAppend The data to append to the existing data
 * @returns Boolean indicating if the update was successful
 */
export async function updateLogEntryAsAdmin(logId: string, dataToAppend: any): Promise<boolean> {
    try {
        const supabase = await createAdminClient()
        
        // First retrieve the current data
        const { data: currentLog, error: fetchError } = await supabase
            .from("logs")
            .select("data")
            .eq("id", logId)
            .single()
        
        if (fetchError) {
            console.error("Failed to fetch log entry:", fetchError, {logId})
            return false
        }
        
        // Merge the existing data with the new data
        const updatedData = { ...currentLog.data, ...dataToAppend }
        
        // Update the log entry with the merged data
        const { error: updateError } = await supabase
            .from("logs")
            .update({
                data: updatedData,
                // Update the status if provided
                ...(dataToAppend.status && { status: dataToAppend.status })
            })
            .eq("id", logId)
        
        if (updateError) {
            console.error("Failed to update log entry:", updateError, {logId, dataToAppend})
            return false
        }
        
        return true
    } catch (err) {
        console.error("Exception in updateLogEntryAsAdmin:", err)
        return false
    }
}

